import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DeleteIcon from '@mui/icons-material/Delete';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import LabelIcon from '@mui/icons-material/Label';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PanToolIcon from '@mui/icons-material/PanTool';
import HighlightAltOutlinedIcon from '@mui/icons-material/HighlightAltOutlined';

import * as turf from '@turf/turf';
import "./MapPage.css";

mapboxgl.accessToken = "pk.eyJ1IjoiamlhY2hlbnpoYW8iLCJhIjoiY200dzJiazU5MGE5YzJqcGt0bmRiM3N2NiJ9.rq1oxS59AJUgdATp8ZP-yw";

function MapPage({ lat, lng, geoJson: initialGeoJson }) {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const draw = useRef(null);

    const [geoJson, setGeoJson] = useState(initialGeoJson || { type: "FeatureCollection", features: [] });
    const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isDrawingRectangle, setIsDrawingRectangle] = useState(false);

    const [isNeutralCursor, setIsNeutralCursor] = useState(true);
    const [copiedPolygons, setCopiedPolygons] = useState([]);
    const [isPasting, setIsPasting] = useState(false);

    useEffect(() => {
        if (!map.current) {
            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: "mapbox://styles/mapbox/satellite-streets-v11",
                center: [lng, lat],
                zoom: 15,
            });

            draw.current = new MapboxDraw({
                displayControlsDefault: false,
                controls: { trash: false },
            });

            map.current.addControl(draw.current);

            map.current.on("load", () => {
                map.current.addSource("geojson", {
                    type: "geojson",
                    data: geoJson,
                });

                // Ensure the overlay is added first
                if (!map.current.getSource("image-overlay")) {
                    map.current.addSource("image-overlay", {
                        type: "image",
                        url: imageOverlay?.url || "",
                        coordinates: imageOverlay?.bounds || [],
                    });

                    map.current.addLayer({
                        id: "image-overlay-layer",
                        type: "raster",
                        source: "image-overlay",
                        paint: {
                            "raster-opacity": overlayOpacity,
                        },
                    });
                }

                // Remove the polygon layer first if it already exists
                if (map.current.getLayer("geojson-layer")) {
                    map.current.removeLayer("geojson-layer");
                }

                // Re-add the polygon layer so it's on top
                map.current.addLayer({
                    id: "geojson-layer",
                    type: "fill",
                    source: "geojson",
                    paint: {
                        "fill-color": "#0080ff",
                        "fill-opacity": 0.5,
                        "fill-outline-color": "#003366",
                    },
                });

                // Log layer order for debugging
                console.log("Layers:", map.current.getStyle().layers);
            });

        }

        const handleDrawDelete = (e) => {
            const remainingFeatures = geoJson.features.filter(
                (feature) => !e.features.some((deleted) => deleted.id === feature.id)
            );
            setGeoJson({ ...geoJson, features: remainingFeatures });
            map.current.getSource("geojson").setData({ ...geoJson, features: remainingFeatures });
        };

        map.current?.on("draw.delete", handleDrawDelete);

        return () => {
            map.current?.off("draw.delete", handleDrawDelete);
        };
    }, [geoJson, lat, lng]);

    useEffect(() => {
        if (!map.current) return;

        const handleRightClick = (e) => {
            const features = map.current.queryRenderedFeatures(e.point, {
                layers: ['geojson-layer'],
            });

            if (features.length > 0) {
                const feature = features[0];
                const properties = feature.properties;
                new mapboxgl.Popup()
                    .setLngLat(e.lngLat)
                    .setHTML(`
                        <div>
                            <h4>Panel Location</h4>
                            <pre>${JSON.stringify(properties, null, 2)}</pre>
                        </div>
                    `)
                    .addTo(map.current);
            }
        };

        map.current.on('contextmenu', handleRightClick);

        return () => {
            map.current.off('contextmenu', handleRightClick);
        };
    }, []);

    const handleToggleSidePanel = () => {
        setIsSidePanelOpen(!isSidePanelOpen);
    };

    const handleDeleteArea = () => {
        let rectangleStart = null;
        let isDrawingRectangle = true;

        map.current.getCanvas().style.cursor = 'crosshair';
        const handleFirstClick = (e) => {
            rectangleStart = [e.lngLat.lng, e.lngLat.lat];
            map.current.on('mousemove', handleMouseMove);
            map.current.once('click', handleSecondClick);
        };
        const handleMouseMove = (e) => {
            if (!rectangleStart) return;
            drawRectanglePreview(rectangleStart, [e.lngLat.lng, e.lngLat.lat]);
        };
        const handleSecondClick = (e) => {
            if (!rectangleStart) return;

            const rectangleEnd = [e.lngLat.lng, e.lngLat.lat];
            const bounds = {
                west: Math.min(rectangleStart[0], rectangleEnd[0]),
                east: Math.max(rectangleStart[0], rectangleEnd[0]),
                south: Math.min(rectangleStart[1], rectangleEnd[1]),
                north: Math.max(rectangleStart[1], rectangleEnd[1]),
            };
            const confirmDelete = window.confirm(
                'Are you sure you want to delete all polygons in this area?'
            );

            if (confirmDelete) {
                deletePolygonsInArea(bounds);
            }
            isDrawingRectangle = false;
            rectangleStart = null;
            map.current.off('mousemove', handleMouseMove);
            map.current.getCanvas().style.cursor = '';
            if (map.current.getSource('preview')) {
                map.current.removeLayer('preview-layer');
                map.current.removeSource('preview');
            }
        };

        map.current.once('click', handleFirstClick);
    };
    const deletePolygonsInArea = (bounds) => {
        const filteredFeatures = geoJson.features.filter((feature) => {
            if (feature.geometry.type !== 'Polygon') return true;
            return feature.geometry.coordinates[0].every(([lng, lat]) => {
                return (
                    lng < bounds.west ||
                    lng > bounds.east ||
                    lat < bounds.south ||
                    lat > bounds.north
                );
            });
        });

        setGeoJson({ ...geoJson, features: filteredFeatures });
        map.current.getSource('geojson').setData({
            ...geoJson,
            features: filteredFeatures,
        });
    };

    const handleSaveEditedGeoJson = () => {
        try {
            const parsedGeoJson = JSON.parse(document.getElementById("geojson-textarea").value);
            if (parsedGeoJson.type === "FeatureCollection") {
                setGeoJson(parsedGeoJson);
                map.current.getSource("geojson").setData(parsedGeoJson);
                setIsEditing(false);
            } else {
                alert("Invalid GeoJSON format. Please ensure it's a FeatureCollection.");
            }
        } catch (error) {
            alert("Error parsing GeoJSON. Please ensure the content is valid JSON.");
        }
    };

    const handleExportGeoJson = () => {
        const blob = new Blob([JSON.stringify(geoJson, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "geojson_data.geojson";
        link.click();
    };

    const handleExitStampMode = (e) => {
        if (e.key === 'Escape') {
            setIsPasting(false);
            setCopiedPolygons([]);
            map.current.getCanvas().style.cursor = '';
            map.current.off('mousemove', handleStampPreview);
            map.current.off('click', handleStampClick);
            window.removeEventListener('keydown', handleExitStampMode);
            if (map.current.getSource('copy-preview-source')) {
                map.current.removeLayer('copy-preview-layer');
                map.current.removeSource('copy-preview-source');
            }

            console.log('Stamp mode exited.');
        }
    };

    const handleNeutralCursor = () => {
        console.log('Neutral cursor activated. All tools reset.');
        map.current.getCanvas().style.cursor = '';
        map.current.off('mousemove');
        map.current.off('click');
        window.removeEventListener('keydown', handleExitStampMode);
        setIsDrawingRectangle(false);
        setIsPasting(false);
        setCopiedPolygons([]);
        setIsNeutralCursor(true);
        ['preview', 'copy-preview-source', 'solar-grid'].forEach((sourceId) => {
            if (map.current.getSource(sourceId)) {
                map.current.removeLayer(`${sourceId}-layer`);
                map.current.removeSource(sourceId);
            }
        });
        if (draw.current) {
            draw.current.changeMode('simple_select');
        }
    };

    const handleZoomIn = () => {
        map.current.zoomIn();
    };

    const handleZoomOut = () => {
        map.current.zoomOut();
    };

    const handleResetMap = () => {
        map.current.flyTo({ center: [lng, lat], zoom: 15 });
    };

    const handleDrawSolarGrid = () => {
        setIsDrawingRectangle(true);
        map.current.getCanvas().style.cursor = 'crosshair';

        let rectangleStart = null;

        const handleFirstClick = (e) => {
            rectangleStart = [e.lngLat.lng, e.lngLat.lat];
            map.current.on('mousemove', handleMouseMove);
            map.current.once('click', handleSecondClick);
        };

        const handleMouseMove = (e) => {
            if (!rectangleStart) return;
            const rectangleEnd = [e.lngLat.lng, e.lngLat.lat];
            drawRectanglePreview(rectangleStart, rectangleEnd);
        };

        const handleSecondClick = (e) => {
            if (!rectangleStart) return;
            const rectangleEnd = [e.lngLat.lng, e.lngLat.lat];

            finalizeRectangle(rectangleStart, rectangleEnd);

            const rows = prompt('Enter number of rows:');
            const columns = prompt('Enter number of columns:');

            if (rows && columns) {
                generateSolarPanelGrid(rectangleStart, rectangleEnd, parseInt(rows), parseInt(columns));
            }
            setIsDrawingRectangle(false);
            rectangleStart = null;
            map.current.off('mousemove', handleMouseMove);
            map.current.getCanvas().style.cursor = '';
        };

        map.current.once('click', handleFirstClick);
    };

    const finalizeRectangle = (start, end) => {
        const rectangle = {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [[
                    [start[0], start[1]],
                    [end[0], start[1]],
                    [end[0], end[1]],
                    [start[0], end[1]],
                    [start[0], start[1]]
                ]]
            },
            properties: { type: 'solar-grid' }
        };
        if (map.current.getSource('solar-grid')) {
            map.current.getSource('solar-grid').setData(rectangle);
        } else {
            map.current.addSource('solar-grid', {
                type: 'geojson',
                data: rectangle
            });

            map.current.addLayer({
                id: 'solar-grid-layer',
                type: 'fill',
                source: 'solar-grid',
                paint: {
                    'fill-color': '#ff0000',
                    'fill-opacity': 0.3,
                    'fill-outline-color': '#aa0000'
                }
            });
        }
        if (map.current.getSource('solar-grid')) {
            map.current.removeLayer('solar-grid-layer');
            map.current.removeSource('solar-grid');
        }
    };

    const drawRectanglePreview = (start, end) => {
        const previewRectangle = {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [[
                    [start[0], start[1]],
                    [end[0], start[1]],
                    [end[0], end[1]],
                    [start[0], end[1]],
                    [start[0], start[1]]
                ]]
            },
            properties: { isPreview: true }
        };

        if (map.current.getSource('preview')) {
            map.current.getSource('preview').setData(previewRectangle);
        } else {
            map.current.addSource('preview', {
                type: 'geojson',
                data: previewRectangle
            });

            map.current.addLayer({
                id: 'preview-layer',
                type: 'fill',
                source: 'preview',
                paint: {
                    'fill-color': '#00ff00',
                    'fill-opacity': 0.3,
                    'fill-outline-color': '#f0ff00'
                }
            });
        }
    };

    const generateSolarPanelGrid = (start, end, rows, columns) => {
        const gridFeatures = [];
        if (rows <= 0 || columns <= 0) {
            alert('Rows and columns must be positive integers.');
            return;
        }

        const latStep = (end[1] - start[1]) / rows;
        const lngStep = (end[0] - start[0]) / columns;
        const precision = 10;

        for (let i = 0; i < rows; i++) {
            for (let j = 0; j < columns; j++) {
                const topLeft = [
                    parseFloat((start[0] + j * lngStep).toFixed(precision)),
                    parseFloat((start[1] + i * latStep).toFixed(precision))
                ];
                const topRight = [
                    parseFloat((start[0] + (j + 1) * lngStep).toFixed(precision)),
                    parseFloat((start[1] + i * latStep).toFixed(precision))
                ];
                const bottomRight = [
                    parseFloat((start[0] + (j + 1) * lngStep).toFixed(precision)),
                    parseFloat((start[1] + (i + 1) * latStep).toFixed(precision))
                ];
                const bottomLeft = [
                    parseFloat((start[0] + j * lngStep).toFixed(precision)),
                    parseFloat((start[1] + (i + 1) * latStep).toFixed(precision))
                ];

                gridFeatures.push({
                    type: 'Feature',
                    geometry: {
                        type: 'Polygon',
                        coordinates: [[
                            topLeft,
                            topRight,
                            bottomRight,
                            bottomLeft,
                            topLeft
                        ]]
                    },
                    properties: {
                    }
                });
            }
        }

        console.log(`Generated ${gridFeatures.length} grid polygons for ${rows}x${columns} grid.`);
        setGeoJson(prev => ({
            ...prev,
            features: [...prev.features, ...gridFeatures]
        }));

        map.current.getSource('geojson').setData({
            type: 'FeatureCollection',
            features: [...geoJson.features, ...gridFeatures]
        });
    };

    const handleStampPreview = (e) => {
        if (!isPasting || copiedPolygons.length === 0) return;
        const targetPoint = [e.lngLat.lng, e.lngLat.lat];
        const offset = calculatePolygonOffset(copiedPolygons[0], targetPoint);
        const previewPolygons = copiedPolygons.map((feature) => ({
            ...feature,
            geometry: {
                ...feature.geometry,
                coordinates: feature.geometry.coordinates.map((ring) =>
                    ring.map(([lng, lat]) => [lng + offset.lng, lat + offset.lat])
                ),
            },
        }));

        if (map.current.getSource('copy-preview-source')) {
            map.current.getSource('copy-preview-source').setData({
                type: 'FeatureCollection',
                features: previewPolygons,
            });
        } else {
            map.current.addSource('copy-preview-source', {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: previewPolygons,
                },
            });

            map.current.addLayer({
                id: 'copy-preview-layer',
                type: 'fill',
                source: 'copy-preview-source',
                paint: {
                    'fill-color': '#ffcc00',
                    'fill-opacity': 0.5,
                    'fill-outline-color': '#ff9900',
                },
            });
        }
    };

    const handleStampClick = (e) => {
        if (!isPasting || copiedPolygons.length === 0) return;

        const targetPoint = [e.lngLat.lng, e.lngLat.lat];
        const offset = calculatePolygonOffset(copiedPolygons[0], targetPoint);

        const stampedPolygons = copiedPolygons.map((feature) => ({
            ...feature,
            geometry: {
                ...feature.geometry,
                coordinates: feature.geometry.coordinates.map((ring) =>
                    ring.map(([lng, lat]) => [lng + offset.lng, lat + offset.lat])
                ),
            },
        }));

        setGeoJson((prev) => ({
            ...prev,
            features: [...prev.features, ...stampedPolygons],
        }));

        map.current.getSource('geojson').setData({
            type: 'FeatureCollection',
            features: [...geoJson.features, ...stampedPolygons],
        });

        handleExitStampMode({ key: 'Escape' });
    };


    const handleCopyStampTool = () => {
        let rectangleStart = null;
        let copiedPolygons = [];
        let isStamping = false;
        let previewSourceId = 'copy-preview-source';
        let previewLayerId = 'copy-preview-layer';

        map.current.getCanvas().style.cursor = 'crosshair';
        const handleFirstClick = (e) => {
            rectangleStart = [e.lngLat.lng, e.lngLat.lat];
            map.current.on('mousemove', handleMouseMove);
            map.current.once('click', handleSecondClick);
        };
        const handleMouseMove = (e) => {
            if (!rectangleStart) return;
            const rectangleEnd = [e.lngLat.lng, e.lngLat.lat];
            drawRectanglePreview(rectangleStart, rectangleEnd);
        };
        const handleSecondClick = (e) => {
            if (!rectangleStart) return;
            const rectangleEnd = [e.lngLat.lng, e.lngLat.lat];
            const bounds = {
                west: Math.min(rectangleStart[0], rectangleEnd[0]),
                east: Math.max(rectangleStart[0], rectangleEnd[0]),
                south: Math.min(rectangleStart[1], rectangleEnd[1]),
                north: Math.max(rectangleStart[1], rectangleEnd[1]),
            };
            copiedPolygons = geoJson.features.filter((feature) => {
                if (feature.geometry.type !== 'Polygon') return false;
                return feature.geometry.coordinates[0].every(([lng, lat]) => {
                    return (
                        lng >= bounds.west &&
                        lng <= bounds.east &&
                        lat >= bounds.south &&
                        lat <= bounds.north
                    );
                });
            });
            if (copiedPolygons.length > 0) {
                const confirmCopy = window.confirm(
                    `You have selected ${copiedPolygons.length} polygons. Start stamping them?`
                );

                if (confirmCopy) {
                    isStamping = true;
                    map.current.getCanvas().style.cursor = 'copy';
                    map.current.on('mousemove', handleStampPreview);
                    map.current.on('click', handleStampClick);
                    window.addEventListener('keydown', handleExitStampMode);
                }
            } else {
                alert('No polygons found in the selected area.');
            }
            rectangleStart = null;
            map.current.off('mousemove', handleMouseMove);
            if (map.current.getSource('preview')) {
                map.current.removeLayer('preview-layer');
                map.current.removeSource('preview');
            }
        };
        const handleStampPreview = (e) => {
            if (!isStamping || copiedPolygons.length === 0) return;

            const targetPoint = [e.lngLat.lng, e.lngLat.lat];
            const offset = calculatePolygonOffset(copiedPolygons[0], targetPoint);

            const previewPolygons = copiedPolygons.map((feature) => ({
                ...feature,
                geometry: {
                    ...feature.geometry,
                    coordinates: feature.geometry.coordinates.map((ring) =>
                        ring.map(([lng, lat]) => [lng + offset.lng, lat + offset.lat])
                    )
                }
            }));

            if (map.current.getSource(previewSourceId)) {
                map.current.getSource(previewSourceId).setData({
                    type: 'FeatureCollection',
                    features: previewPolygons,
                });
            } else {
                map.current.addSource(previewSourceId, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: previewPolygons,
                    },
                });

                map.current.addLayer({
                    id: previewLayerId,
                    type: 'fill',
                    source: previewSourceId,
                    paint: {
                        'fill-color': '#ffcc00',
                        'fill-opacity': 0.5,
                        'fill-outline-color': '#ff9900',
                    },
                });
            }
        };
        const handleStampClick = (e) => {
            if (!isStamping || copiedPolygons.length === 0) return;

            const targetPoint = [e.lngLat.lng, e.lngLat.lat];
            const offset = calculatePolygonOffset(copiedPolygons[0], targetPoint);

            const stampedPolygons = copiedPolygons.map((feature) => ({
                ...feature,
                geometry: {
                    ...feature.geometry,
                    coordinates: feature.geometry.coordinates.map((ring) =>
                        ring.map(([lng, lat]) => [lng + offset.lng, lat + offset.lat])
                    )
                }
            }));

            setGeoJson(prev => ({
                ...prev,
                features: [...prev.features, ...stampedPolygons]
            }));

            map.current.getSource('geojson').setData({
                ...geoJson,
                features: [...geoJson.features, ...stampedPolygons]
            });

            handleExitStampMode({ key: 'Escape' });
        };
        const handleExitStampMode = (e) => {
            if (e.key === 'Escape') {
                isStamping = false;
                map.current.off('mousemove', handleStampPreview);
                map.current.off('click', handleStampClick);
                window.removeEventListener('keydown', handleExitStampMode);
                map.current.getCanvas().style.cursor = '';

                if (map.current.getSource(previewSourceId)) {
                    map.current.removeLayer(previewLayerId);
                    map.current.removeSource(previewSourceId);
                }
            }
        };

        map.current.once('click', handleFirstClick);
    };

    /**
 * Calculate the offset between the first polygon's reference point and the target point.
 * @param {Object} polygon - The first polygon from the copied selection.
 * @param {Array} targetPoint - The [lng, lat] of the cursor location.
 * @returns {Object} - Offset values { lng, lat }.
 */
    const calculatePolygonOffset = (polygon, targetPoint) => {
        if (!polygon || !polygon.geometry || !polygon.geometry.coordinates) {
            console.warn('Invalid polygon data for offset calculation.');
            return { lng: 0, lat: 0 };
        }
        const referencePoint = polygon.geometry.coordinates[0][0];
        const offset = {
            lng: targetPoint[0] - referencePoint[0],
            lat: targetPoint[1] - referencePoint[1],
        };

        return offset;
    };

    const handleLabelingTool = () => {
        console.log('Labeling tool activated. Select two points to define the labeling area.');

        map.current.getCanvas().style.cursor = 'crosshair';
        let rectangleStart = null;
        const handleFirstClick = (e) => {
            rectangleStart = [e.lngLat.lng, e.lngLat.lat];
            map.current.on('mousemove', handleMouseMove);
            map.current.once('click', handleSecondClick);
        };
        const handleMouseMove = (e) => {
            if (!rectangleStart) return;
            drawRectanglePreview(rectangleStart, [e.lngLat.lng, e.lngLat.lat]);
        };
        const handleSecondClick = (e) => {
            if (!rectangleStart) return;

            const rectangleEnd = [e.lngLat.lng, e.lngLat.lat];
            const bounds = {
                west: Math.min(rectangleStart[0], rectangleEnd[0]),
                east: Math.max(rectangleStart[0], rectangleEnd[0]),
                south: Math.min(rectangleStart[1], rectangleEnd[1]),
                north: Math.max(rectangleStart[1], rectangleEnd[1]),
            };
            const selectedPolygons = geoJson.features.filter((feature) => {
                if (feature.geometry.type !== 'Polygon') return false;

                return feature.geometry.coordinates[0].every(([lng, lat]) => {
                    return (
                        lng >= bounds.west &&
                        lng <= bounds.east &&
                        lat >= bounds.south &&
                        lat <= bounds.north
                    );
                });
            });

            if (selectedPolygons.length === 0) {
                alert('No polygons selected in the drawn area.');
                cleanupTool();
                return;
            }
            const inverterBlock = prompt('Enter Inverter Block (e.g., 12):', '');
            const group = prompt('Enter Group (e.g., 3):', '');

            if (!inverterBlock || isNaN(inverterBlock) || !group || isNaN(group)) {
                alert('Invalid input. Inverter Block and Group must be numbers.');
                cleanupTool();
                return;
            }
            const sortedPolygons = defineRowsAndColumns(selectedPolygons);
            let updatedFeatures = geoJson.features.map((feature) => {
                const matchingPolygon = sortedPolygons.find(
                    (sp) => sp.geometry.coordinates[0][0][0] === feature.geometry.coordinates[0][0][0] &&
                        sp.geometry.coordinates[0][0][1] === feature.geometry.coordinates[0][0][1]
                );

                if (matchingPolygon) {
                    return {
                        ...feature,
                        properties: {
                            ...feature.properties,
                            location: `IB${inverterBlock}-G${group}-R${matchingPolygon.row}N-C${matchingPolygon.column}W`,
                        },
                    };
                }
                return feature;
            });
            setGeoJson({ ...geoJson, features: updatedFeatures });
            map.current.getSource('geojson').setData({
                type: 'FeatureCollection',
                features: updatedFeatures,
            });

            alert(`Labeled ${selectedPolygons.length} polygons with IB${inverterBlock}-G${group}.`);
            cleanupTool();
        };
        const cleanupTool = () => {
            rectangleStart = null;
            map.current.off('mousemove', handleMouseMove);
            map.current.getCanvas().style.cursor = '';
            if (map.current.getSource('preview')) {
                map.current.removeLayer('preview-layer');
                map.current.removeSource('preview');
            }
            console.log('Labeling tool deactivated.');
        };
        const drawRectanglePreview = (start, end) => {
            const previewRectangle = {
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [[
                        [start[0], start[1]],
                        [end[0], start[1]],
                        [end[0], end[1]],
                        [start[0], end[1]],
                        [start[0], start[1]]
                    ]]
                },
                properties: { isPreview: true }
            };

            if (map.current.getSource('preview')) {
                map.current.getSource('preview').setData(previewRectangle);
            } else {
                map.current.addSource('preview', {
                    type: 'geojson',
                    data: previewRectangle
                });

                map.current.addLayer({
                    id: 'preview-layer',
                    type: 'fill',
                    source: 'preview',
                    paint: {
                        'fill-color': '#00ff00',
                        'fill-opacity': 0.3,
                        'fill-outline-color': '#00ff00'
                    }
                });
            }
        };
        map.current.once('click', handleFirstClick);
    };

    /**
     * Define rows and columns for polygons based on centroid latitude and longitude.
     * @param {Array} polygons - Array of polygon features from GeoJSON.
     * @returns {Array} - Polygons annotated with row and column properties.
     */
    const defineRowsAndColumns = (polygons) => {
        if (!polygons || polygons.length === 0) return [];

        // Step 1: Calculate centroids
        const calculateCentroid = (coordinates) => {
            let lngSum = 0, latSum = 0, count = 0;
            coordinates[0].forEach(([lng, lat]) => {
                lngSum += lng;
                latSum += lat;
                count++;
            });
            return [lngSum / count, latSum / count];
        };

        const polygonsWithCentroids = polygons.map((polygon) => ({
            ...polygon,
            centroid: calculateCentroid(polygon.geometry.coordinates),
        }));

        const uniqueLatitudes = [
            ...new Set(polygonsWithCentroids.map((p) => p.centroid[1]))
        ].sort((a, b) => b - a); // North to South (Descending)

        const uniqueLongitudes = [
            ...new Set(polygonsWithCentroids.map((p) => p.centroid[0]))
        ].sort((a, b) => a - b); // West to East (Ascending)

        const annotatedPolygons = polygonsWithCentroids.map((polygon) => {
            const row = uniqueLatitudes.indexOf(polygon.centroid[1]) + 1;
            const column = uniqueLongitudes.indexOf(polygon.centroid[0]) + 1;

            return {
                ...polygon,
                row,
                column,
            };
        });

        console.log(`Assigned ${annotatedPolygons.length} polygons to rows and columns.`);
        return annotatedPolygons;
    };

    const handleDrawInverterBlock = () => {
        console.log('Inverter Block Drawing Tool Activated');

        // State variables
        let isDrawing = false;
        let currentPolygon = [];
        let currentVertexIndex = null;

        const drawingWindow = document.createElement('div');
        drawingWindow.id = 'drawing-window';
        drawingWindow.innerHTML = `
            <div style="position: fixed; top: 100px; left: 50%; transform: translateX(-50%); background: white; border: 1px solid #ccc; padding: 10px; z-index: 1000;">
                <button id="save-polygon">Save</button>
                <button id="cancel-polygon">Cancel</button>
            </div>
        `;
        document.body.appendChild(drawingWindow);

        map.current.getCanvas().style.cursor = 'crosshair';

        const startDrawing = (e) => {
            const clickedPoint = [e.lngLat.lng, e.lngLat.lat];

            if (!isDrawing) {
                isDrawing = true;
                currentPolygon.push(clickedPoint);
                map.current.on('mousemove', previewPolygon);
            } else {
                if (isClickingStartVertex(clickedPoint)) {
                    completePolygon();
                    return;
                }

                currentPolygon.push(clickedPoint);
                updatePolygonPreview();
                updateVertexDots();
            }
        };

        const previewPolygon = (e) => {
            if (!isDrawing) return;

            const tempPolygon = [...currentPolygon, [e.lngLat.lng, e.lngLat.lat]];
            drawPolygonPreview(tempPolygon);
        };

        const completePolygon = () => {
            if (currentPolygon.length < 3) {
                alert('A polygon requires at least 3 points.');
                cleanupDrawing();
                return;
            }

            currentPolygon.push(currentPolygon[0]);
            isDrawing = false;
            map.current.off('mousemove', previewPolygon);
            map.current.off('click', startDrawing);
            drawPolygonPreview(currentPolygon, true);
            enableVertexDragging();
        };

        const enableVertexDragging = () => {
            map.current.dragPan.disable(); // Disable map dragging

            map.current.on('mousedown', (e) => {
                const clickedPoint = [e.lngLat.lng, e.lngLat.lat];
                currentVertexIndex = findClosestVertex(clickedPoint);

                if (currentVertexIndex !== null) {
                    highlightVertex(currentVertexIndex);
                }
            });

            map.current.on('mousemove', (e) => {
                if (currentVertexIndex === null) return;

                currentPolygon[currentVertexIndex] = [e.lngLat.lng, e.lngLat.lat];
                drawPolygonPreview(currentPolygon, true);
                updateVertexDots();
            });

            map.current.on('mouseup', () => {
                currentVertexIndex = null;
                removeVertexHighlight();
            });
        };

        const savePolygon = () => {
            const newPolygonFeature = {
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [currentPolygon],
                },
                properties: {
                    type: 'inverter-block-outline',
                },
            };

            setGeoJson((prev) => ({
                ...prev,
                features: [...prev.features, newPolygonFeature],
            }));

            map.current.getSource('geojson').setData({
                type: 'FeatureCollection',
                features: [...geoJson.features, newPolygonFeature],
            });
            cleanupDrawing();
        };

        const cancelPolygon = () => {
            cleanupDrawing();
        };

        const cleanupDrawing = () => {
            isDrawing = false;
            currentPolygon = [];
            currentVertexIndex = null;

            map.current.off('click', startDrawing);
            map.current.off('mousemove', previewPolygon);
            map.current.off('mousedown');
            map.current.off('mousemove');
            map.current.off('mouseup');

            map.current.dragPan.enable();

            map.current.getCanvas().style.cursor = '';
            ['preview-polygon', 'vertex-dots'].forEach((sourceId) => {
                if (map.current.getSource(sourceId)) {
                    map.current.removeLayer(`${sourceId}-layer`);
                    map.current.removeSource(sourceId);
                }
            });

            document.body.removeChild(drawingWindow);
        };

        const drawPolygonPreview = (polygon, isFinal = false) => {
            const previewPolygon = {
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [polygon],
                },
                properties: { isPreview: true },
            };

            if (map.current.getSource('preview-polygon')) {
                map.current.getSource('preview-polygon').setData(previewPolygon);
            } else {
                map.current.addSource('preview-polygon', {
                    type: 'geojson',
                    data: previewPolygon,
                });

                map.current.addLayer({
                    id: 'preview-polygon-layer',
                    type: 'fill',
                    source: 'preview-polygon',
                    paint: {
                        'fill-color': isFinal ? '#FF8800' : '#00FF00',
                        'fill-opacity': 0.5,
                        'fill-outline-color': '#FF5500',
                    },
                });
            }
        };

        const updateVertexDots = () => {
            const vertexPoints = currentPolygon.map(([lng, lat]) => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [lng, lat] },
            }));

            if (map.current.getSource('vertex-dots')) {
                map.current.getSource('vertex-dots').setData({
                    type: 'FeatureCollection',
                    features: vertexPoints,
                });
            } else {
                map.current.addSource('vertex-dots', {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: vertexPoints,
                    },
                });

                map.current.addLayer({
                    id: 'vertex-dots-layer',
                    type: 'circle',
                    source: 'vertex-dots',
                    paint: {
                        'circle-radius': 5,
                        'circle-color': '#FF0000',
                        'circle-stroke-width': 1,
                        'circle-stroke-color': '#FFFFFF',
                    },
                });
            }
        };

        const isClickingStartVertex = ([lng, lat]) => {
            const [startLng, startLat] = currentPolygon[0];
            return Math.hypot(lng - startLng, lat - startLat) < 0.0001;
        };


        /**
         * Highlights a specific vertex on the map.
         * @param {number} index - Index of the vertex to highlight.
         */
        const highlightVertex = (index) => {
            const vertex = currentPolygon[index];
            if (!vertex) return;

            const highlightedVertex = {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: vertex,
                },
            };

            if (map.current.getSource('highlighted-vertex')) {
                map.current.getSource('highlighted-vertex').setData(highlightedVertex);
            } else {
                map.current.addSource('highlighted-vertex', {
                    type: 'geojson',
                    data: highlightedVertex,
                });

                map.current.addLayer({
                    id: 'highlighted-vertex-layer',
                    type: 'circle',
                    source: 'highlighted-vertex',
                    paint: {
                        'circle-radius': 15,
                        'circle-color': '#FFD700',
                        'circle-stroke-width': 3,
                        'circle-stroke-color': '#FFFFFF',
                    },
                });
            }
        };

        /**
     * Finds the closest vertex within a defined radius.
     * @param {Array} point - [lng, lat] of the clicked point.
     * @returns {number|null} - Index of the closest vertex, or null if none found.
     */
        const findClosestVertex = (point) => {
            let closestIndex = null;
            let minDistance = Infinity;
            const clickRadius = getClickRadius();

            currentPolygon.forEach(([lng, lat], index) => {
                const distance = calculateDistance(point, [lng, lat]);
                if (distance < clickRadius && distance < minDistance) {
                    minDistance = distance;
                    closestIndex = index;
                }
            });

            if (closestIndex !== null) {
                highlightVertex(closestIndex);
            }

            return closestIndex;
        };

        const updatePolygonPreview = () => {
            if (currentPolygon.length < 2) return;

            const previewPolygon = {
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [currentPolygon],
                },
                properties: { isPreview: true },
            };

            if (map.current.getSource('preview-polygon')) {
                map.current.getSource('preview-polygon').setData(previewPolygon);
            } else {
                map.current.addSource('preview-polygon', {
                    type: 'geojson',
                    data: previewPolygon,
                });

                map.current.addLayer({
                    id: 'preview-polygon-layer',
                    type: 'fill',
                    source: 'preview-polygon',
                    paint: {
                        'fill-color': '#00FF00',
                        'fill-opacity': 0.5,
                        'fill-outline-color': '#FF5500',
                    },
                });
            }
        };

        map.current.on('click', startDrawing);
        map.current.once('dblclick', completePolygon);

        document.getElementById('save-polygon').onclick = savePolygon;
        document.getElementById('cancel-polygon').onclick = cancelPolygon;
    };

    /**
     * Calculates the distance between two geographic points in meters.
     * @param {Array} point1 - [lng, lat] of the first point.
     * @param {Array} point2 - [lng, lat] of the second point.
     * @returns {number} - Distance in meters.
     */
    const calculateDistance = ([lng1, lat1], [lng2, lat2]) => {
        const R = 6371000; // Earth radius in meters
        const rad = Math.PI / 180;
        const dLat = (lat2 - lat1) * rad;
        const dLng = (lng2 - lng1) * rad;
        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    /**
     * Gets a dynamic click radius based on the current zoom level.
     * @returns {number} - Click radius in meters.
     */
    const getClickRadius = () => {
        const zoom = map.current.getZoom();
        if (zoom > 15) return 15;
        if (zoom > 13) return 25;
        return 20;
    };

    const removeVertexHighlight = () => {
        if (map.current.getSource('highlighted-vertex')) {
            map.current.removeLayer('highlighted-vertex-layer');
            map.current.removeSource('highlighted-vertex');
        }
    };

    const handleImageDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];

        if (file && (file.type.startsWith("image/") || file.name.match(/\.(png|jpg|jpeg)$/))) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const imageURL = event.target.result;

                const img = new Image();
                img.onload = () => {
                    const imageWidth = img.width;
                    const imageHeight = img.height;

                    const center = map.current.getCenter();
                    const initialBounds = [
                        [center.lng - 0.005, center.lat + 0.005],
                        [center.lng + 0.005, center.lat + 0.005],
                        [center.lng + 0.005, center.lat - 0.005],
                        [center.lng - 0.005, center.lat - 0.005],
                    ];

                    setImageOverlay({
                        url: imageURL,
                        bounds: initialBounds,
                        width: imageWidth,
                        height: imageHeight
                    });

                    addImageOverlay(imageURL, initialBounds);
                };

                img.src = imageURL;
            };
            reader.readAsDataURL(file);
        } else {
            alert("Please upload a valid image file (PNG, JPG, JPEG).");
        }
    };


    const toggleImageOverlay = () => {
        if (!map.current.getLayer("image-overlay-layer")) return;

        const visibility = map.current.getLayoutProperty("image-overlay-layer", "visibility");
        if (visibility === "visible") {
            map.current.setLayoutProperty("image-overlay-layer", "visibility", "none");
        } else {
            map.current.setLayoutProperty("image-overlay-layer", "visibility", "visible");
        }
    };

    const addImageOverlay = (imageURL, bounds) => {
        if (!map.current) return;

        if (!map.current.getSource("image-overlay")) {
            map.current.addSource("image-overlay", {
                type: "image",
                url: imageURL,
                coordinates: bounds,
            });

            map.current.addLayer({
                id: "image-overlay-layer",
                type: "raster",
                source: "image-overlay",
                paint: {
                    "raster-opacity": overlayOpacity,
                },
            });
        }

        if (map.current.getLayer("geojson-layer")) {
            map.current.removeLayer("geojson-layer");
        }

        map.current.addLayer({
            id: "geojson-layer",
            type: "fill",
            source: "geojson",
            paint: {
                "fill-color": "#0080ff",
                "fill-opacity": 0.5,
                "fill-outline-color": "#003366",
            },
        });
    };

    const [isBoundsModalOpen, setIsBoundsModalOpen] = useState(false);
    const [boundsInput, setBoundsInput] = useState({ north: "", south: "", east: "", west: "" });
    const handleBoundsSubmit = () => {
        const { north, south, east, west } = boundsInput;
        if (!north || !south || !east || !west) {
            alert("Please fill in all fields with valid coordinates.");
            return;
        }

        const northVal = parseFloat(north);
        const southVal = parseFloat(south);
        const eastVal = parseFloat(east);
        const westVal = parseFloat(west);

        if (isNaN(northVal) || isNaN(southVal) || isNaN(eastVal) || isNaN(westVal)) {
            alert("Invalid coordinates. Please enter numeric values.");
            return;
        }

        const newBounds = [
            [westVal, northVal],
            [eastVal, northVal],
            [eastVal, southVal],
            [westVal, southVal],
        ];


        if (imageOverlay) {
            map.current.getSource("image-overlay").updateImage({
                url: imageOverlay.url,
                coordinates: newBounds,
            });
            setImageOverlay({ ...imageOverlay, bounds: newBounds });
        }

        setIsBoundsModalOpen(false);
    };

    const handleBoundsInputChange = (field, value) => {
        setBoundsInput((prev) => ({ ...prev, [field]: value }));
    };

    const [overlayOpacity, setOverlayOpacity] = useState(1);

    useEffect(() => {
        if (map.current && map.current.getLayer("image-overlay-layer")) {
            map.current.setPaintProperty("image-overlay-layer", "raster-opacity", overlayOpacity);
        }
    }, [overlayOpacity]);


    const handleJGWFileDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];

        if (file && file.name.endsWith(".jgw")) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const content = event.target.result;
                const bounds = parseJGW(content);
                if (bounds) {
                    setBoundsInput({
                        north: bounds[0][1],
                        south: bounds[2][1],
                        east: bounds[2][0],
                        west: bounds[0][0],
                    });
                } else {
                    alert("Invalid .jgw file format.");
                }
            };
            reader.readAsText(file);
        } else {
            alert("Please upload a valid .jgw file.");
        }
    };
    const [imageOverlay, setImageOverlay] = useState(null);

    const parseJGW = (content) => {
        const lines = content.trim().split("\n");
        if (lines.length !== 6) {
            return null;
        }

        const pixelSizeX = parseFloat(lines[0]);
        const pixelSizeY = parseFloat(lines[3]);
        const upperLeftX = parseFloat(lines[4]);
        const upperLeftY = parseFloat(lines[5]);

        if (isNaN(pixelSizeX) || isNaN(pixelSizeY) || isNaN(upperLeftX) || isNaN(upperLeftY)) {
            return null;
        }

        const imageWidth = imageOverlay.width;
        const imageHeight = imageOverlay.height;

        const lowerRightX = upperLeftX + pixelSizeX * imageWidth;
        const lowerRightY = upperLeftY + pixelSizeY * imageHeight;

        const convertToEPSG4326 = (x, y) => {
            const R = 6378137;
            const lon = (x / R) * (180 / Math.PI);
            const lat = (2 * Math.atan(Math.exp(y / R)) - Math.PI / 2) * (180 / Math.PI);
            return [parseFloat(lon.toFixed(12)), parseFloat(lat.toFixed(12))];
        };

        const upperLeft = convertToEPSG4326(upperLeftX, upperLeftY);
        const lowerRight = convertToEPSG4326(lowerRightX, lowerRightY);

        return [
            [upperLeft[0], upperLeft[1]],
            [lowerRight[0], upperLeft[1]],
            [lowerRight[0], lowerRight[1]],
            [upperLeft[0], lowerRight[1]],
        ];
    };

    //HEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHz
    //HEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHz
    //HEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHz
    //HEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHzHEHEHEHEHEHEEHEEHEHEHEHEHz

    const handleInteractiveRotate = () => {
        console.log('Interactive Rotate Tool Activated');

        map.current.getCanvas().style.cursor = 'crosshair';
        let rectangleStart = null;
        let selectedPolygons = [];

        const handleFirstClick = (e) => {
            rectangleStart = [e.lngLat.lng, e.lngLat.lat];
            map.current.on('mousemove', handleMouseMove);
            map.current.once('click', handleSecondClick);
        };

        const handleMouseMove = (e) => {
            if (!rectangleStart) return;
            drawRectanglePreview(rectangleStart, [e.lngLat.lng, e.lngLat.lat]);
        };

        const handleSecondClick = (e) => {
            const rectangleEnd = [e.lngLat.lng, e.lngLat.lat];
            const bounds = {
                west: Math.min(rectangleStart[0], rectangleEnd[0]),
                east: Math.max(rectangleStart[0], rectangleEnd[0]),
                south: Math.min(rectangleStart[1], rectangleEnd[1]),
                north: Math.max(rectangleStart[1], rectangleEnd[1]),
            };

            selectedPolygons = geoJson.features.filter((feature) => {
                if (feature.geometry.type !== 'Polygon') return false;
                return feature.geometry.coordinates[0].every(([lng, lat]) => (
                    lng >= bounds.west &&
                    lng <= bounds.east &&
                    lat >= bounds.south &&
                    lat <= bounds.north
                ));
            });

            if (selectedPolygons.length === 0) {
                alert('No polygons found in selection.');
                cleanup();
                return;
            }

            const clusterCentroid = turf.center({
                type: 'FeatureCollection',
                features: selectedPolygons
            });

            console.log("Selected polygons: ", selectedPolygons.length);
            console.log("Centroid for the cluster: ", clusterCentroid.geometry.coordinates);
            showRotationUI(clusterCentroid.geometry.coordinates, selectedPolygons);
            cleanup();
        };

        const cleanup = () => {
            rectangleStart = null;
            map.current.off('mousemove', handleMouseMove);
            map.current.getCanvas().style.cursor = '';
            if (map.current.getSource('preview')) {
                map.current.removeLayer('preview-layer');
                map.current.removeSource('preview');
            }
        };

        map.current.once('click', handleFirstClick);
    };

    const showRotationUI = (center, polygons) => {
        const radius = 0.001;
        const handlePosition = [center[0], center[1] + radius];

        if (map.current.getSource('rotation-handle')) {
            map.current.removeLayer('rotation-handle-layer');
            map.current.removeSource('rotation-handle');
        }

        map.current.addSource('rotation-handle', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: [{
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates: handlePosition
                    }
                }]
            }
        });

        map.current.addLayer({
            id: 'rotation-handle-layer',
            type: 'circle',
            source: 'rotation-handle',
            paint: {
                'circle-radius': 8,
                'circle-color': '#ff0000',
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff'
            }
        });

        let isRotating = false;
        let initialAngle = 0;
        let rotatedFeatures = polygons;

        const getAngleFromCenter = (lngLat) => {
            const dx = lngLat.lng - center[0];
            const dy = lngLat.lat - center[1];
            return Math.atan2(dy, dx) * (180 / Math.PI); // degrees
        };

        const onMouseMove = (e) => {
            if (!isRotating) return;

            const currentAngle = getAngleFromCenter(e.lngLat);
            const angleDelta = currentAngle - initialAngle;

            const rotated = turf.transformRotate(
                {
                    type: 'FeatureCollection',
                    features: polygons,
                },
                angleDelta,
                { pivot: center }
            );

            rotatedFeatures = rotated.features;

            const newGeo = {
                ...geoJson,
                features: [
                    ...geoJson.features.filter(f => !polygons.includes(f)),
                    ...rotated.features
                ]
            };

            setGeoJson(newGeo);
            map.current.getSource('geojson').setData(newGeo);
        };

        const onKeyDown = (e) => {
            if (e.code === 'Space') {
                console.log('Rotation confirmed.');
                finalizeRotation();
            } else if (e.code === 'Escape') {
                console.log('Rotation canceled. Reverting.');
                revertToOriginal();
            }
        };

        const finalizeRotation = () => {
            map.current.getCanvas().style.cursor = '';
            isRotating = false;
            window.removeEventListener('keydown', onKeyDown);
            map.current.off('mousemove', onMouseMove);
            removeRotationUI();
        };

        const revertToOriginal = () => {
            const reverted = {
                ...geoJson,
                features: [
                    ...geoJson.features.filter(f => !rotatedFeatures.includes(f)),
                    ...polygons
                ]
            };
            setGeoJson(reverted);
            map.current.getSource('geojson').setData(reverted);

            map.current.getCanvas().style.cursor = '';
            isRotating = false;
            window.removeEventListener('keydown', onKeyDown);
            map.current.off('mousemove', onMouseMove);
            removeRotationUI();
        };

        const onClick = () => {
            if (!isRotating) {
                console.log('Entering rotation mode...');
                isRotating = true;
                map.current.getCanvas().style.cursor = 'alias';
                map.current.once('mousemove', (e) => {
                    initialAngle = getAngleFromCenter(e.lngLat);
                });

                map.current.on('mousemove', onMouseMove);
                window.addEventListener('keydown', onKeyDown);
            }
        };

        map.current.on('click', 'rotation-handle-layer', onClick);
    };


    const removeRotationUI = () => {
        if (map.current.getSource('rotation-handle')) {
            map.current.removeLayer('rotation-handle-layer');
            map.current.removeSource('rotation-handle');
        }
    };


    return (
        <div className="page-wrapper">

            {isBoundsModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-box">
                        <h3>Set Overlay Parameters</h3>
                        <p>Enter coorisponding lat/lng to set orthomosaic boundaries:</p>
                        <div>
                            <label>North: </label>
                            <input
                                type="text"
                                value={boundsInput.north}
                                onChange={(e) => handleBoundsInputChange("north", e.target.value)}
                            />
                        </div>
                        <div>
                            <label>South: </label>
                            <input
                                type="text"
                                value={boundsInput.south}
                                onChange={(e) => handleBoundsInputChange("south", e.target.value)}
                            />
                        </div>
                        <div>
                            <label>East: </label>
                            <input
                                type="text"
                                value={boundsInput.east}
                                onChange={(e) => handleBoundsInputChange("east", e.target.value)}
                            />
                        </div>
                        <div>
                            <label>West: </label>
                            <input
                                type="text"
                                value={boundsInput.west}
                                onChange={(e) => handleBoundsInputChange("west", e.target.value)}
                            />
                        </div>
                        <div>
                            <label>Overlay Opacity: </label>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                value={overlayOpacity}
                                onChange={(e) => setOverlayOpacity(parseFloat(e.target.value))}
                            />
                            <span>{Math.round(overlayOpacity * 100)}%</span>
                        </div>
                        <div
                            className="drag-drop-jgw"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleJGWFileDrop}
                        >
                            <p>If you have a .jgw file drag it here to line up the image</p>
                        </div>
                        <div className="modal-buttons">
                            <button onClick={() => setIsBoundsModalOpen(false)}>Cancel</button>
                            <button onClick={handleBoundsSubmit}>Set Bounds</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Main Map Page */}
            <div className="map-page">

                {/* Map Container */}
                <div ref={mapContainer} className="map-container">
                    <div className={`toolbar ${isSidePanelOpen ? 'shifted' : ''}`}>

                        <button className="map-button" onClick={handleNeutralCursor} aria-label="Neutral Cursor" title="Neutral Cursor">
                            <PanToolIcon />
                        </button>
                        <button className="map-button" onClick={handleZoomIn} aria-label="Zoom In" title="Zoom In">
                            <ZoomInIcon />
                        </button>
                        <button className="map-button" onClick={handleZoomOut} aria-label="Zoom Out" title="Zoom Out">
                            <ZoomOutIcon />
                        </button>
                        <button className="map-button" onClick={handleResetMap} aria-label="Center Map" title="Center Map">
                            <GpsFixedIcon />
                        </button>
                        <button className="map-button" onClick={handleDrawSolarGrid} aria-label="Draw Solar Grid" title="Draw Solar Grid">
                            <CropSquareIcon />
                        </button>
                        <button className="map-button" onClick={handleCopyStampTool} aria-label="Copy & Stamp Tool" title="Copy & Stamp Tool">
                            <FileCopyIcon />
                        </button>
                        <button className="map-button" onClick={handleDeleteArea} aria-label="Delete Selected Area" title="Delete Selected Area">
                            <DeleteIcon />
                        </button>
                        <button className="map-button" onClick={handleLabelingTool} aria-label="Label Polygons" title="Label Polygons in Selected Area">
                            <LabelIcon />
                        </button>
                        <button className="map-button" onClick={handleDrawInverterBlock} aria-label="Draw Inverter Block Outline" title="Draw Inverter Block Outline">
                            <HighlightAltOutlinedIcon />
                        </button>
                        <button
                            className="map-button"
                            onClick={handleInteractiveRotate}
                            aria-label="Rotate Cluster"
                            title="Rotate Cluster"
                        >
                            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>⟳</span>
                        </button>


                    </div>

                    <div
                        className="drag-drop-overlay"
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleImageDrop}
                    >
                        Drag and drop image overlay
                    </div>
                    <div className="overlay-toggle">
                        <button onClick={toggleImageOverlay}>Toggle Image Overlay</button>
                    </div>

                    <div className="set-bounds">
                        <button onClick={() => setIsBoundsModalOpen(true)}>Overlay Parameters</button>
                    </div>
                </div>

                <div
                    className={`hamburger-icon ${isSidePanelOpen ? "shifted" : ""}`}
                    onClick={handleToggleSidePanel}
                    title="View GeoJSON"
                >
                    &#9776;
                </div>

                <div className={`side-panel ${isSidePanelOpen ? "open" : ""}`}>
                    <h3>GeoJSON Content</h3>
                    {isEditing ? (
                        <>
                            <button onClick={handleSaveEditedGeoJson}>Save</button>
                            <button onClick={() => setIsEditing(false)}>Cancel</button>
                            <textarea
                                id="geojson-textarea"
                                defaultValue={JSON.stringify(geoJson, null, 2)}
                                className="geojson-textarea"
                            ></textarea>

                        </>
                    ) : (
                        <>
                            <div className="button-container">
                                <button onClick={() => setIsEditing(true)}>
                                    <span className="button-icon">✏️</span> Edit GeoJSON
                                </button>
                                <button onClick={handleExportGeoJson}>
                                    <span className="button-icon">⬇️</span> Export GeoJSON
                                </button>
                            </div>
                            <pre>{JSON.stringify(geoJson, null, 2)}</pre>
                        </>
                    )}
                </div>
            </div>
        </div>
    );

}

export default MapPage;
